import { orderBy } from 'lodash';

import { RESET_ALL as RESET_ON_LOGOUT, danger, success } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { createReducer } from 'utils/redux';
import { API, get, put } from 'utils/api';
import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';
import {
  DELIVERABLE_ADDITIONAL_COSTS,
  DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION,
  DELIVERABLE_TYPE_BLOG,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_OTHER,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO
} from 'utils/deliverables';

import type {
  Action,
  DeliverableType,
  Dispatch,
  GetState,
  ThunkAction
} from 'types';
import type { BudgetSplitResourceV2 } from '../types/BudgetSplitResource.type';
import type { CampaignCostAttributionReducerState } from '../types/CampaignCostAttributionReducer.type';

applyTranslations(require('./campaign-cost-attribution-reducer.i18n.json'));

const { REPORTS } = API.END_POINTS;

const LOADING = 'campaignCostAttribution/LOADING';
const LOADED = 'campaignCostAttribution/LOADED';
const RESET = 'campaignCostAttribution/RESET';

const initialState: CampaignCostAttributionReducerState = {
  results: [],
  isLoading: false
};

const splitsLoaded = (results: Array<BudgetSplitResourceV2>): Action => ({
  type: LOADED,

  payload: {
    results
  }
});

const splitsLoading = (isLoading: boolean): Action => ({
  type: LOADING,

  payload: {
    isLoading
  }
});

export const resetSplits = (): Action => ({
  type: RESET
});

export const fetchSplits = (campaignId: number): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(splitsLoading(true));

  try {
    const url = REPORTS.budgetSplitsV2(campaignId);
    const results = await get(url, {}, { suppressGlobalNotification: true });

    dispatch(splitsLoading(false));
    dispatch(splitsLoaded(orderBy(results.data, ['creator.metrics[0].followers'], ['desc'])));
  } catch (e) {
    logError(e);
    dispatch(danger(__t('campaign_cost_attribution_reducer.fetch_error')));
  } finally {
    dispatch(splitsLoading(false));
  }
};


type MappedAttributionType = {
  [x: string]: string
};

type MappedAttributionsType = {
  [x: string]: MappedAttributionType
};

const getSplitMetric = (deliverableType: string, attribution: MappedAttributionType): number => parseFloat(attribution[deliverableType]) || 0;

const getDeliverableCount = (deliverableType: DeliverableType, attribution: BudgetSplitResourceV2): number => attribution.budgetSplit.taskSplits
  .find((task) => task.deliverableType === deliverableType)?.count ?? 0;

export const updateSplits = (newAttributions: MappedAttributionsType): ThunkAction => async (dispatch: Dispatch, getState: GetState) => {
  const currentAttribution = getState().campaignCostAttribution.results;
  const url = REPORTS.saveBudgetSplit();
  dispatch(showLoading(url));

  try {
    const mappedData = currentAttribution.map((attribution) => {
      const {
        id,
        offerId,
        influencerId,
        isOutdated,
        totalBudget
      } = attribution.budgetSplit;
      const creatorAttribution = newAttributions[String(attribution.creator.id)];

      return {
        id,
        offerId,
        influencerId,
        isOutdated,
        totalBudget,
        additionalCosts: getSplitMetric(DELIVERABLE_ADDITIONAL_COSTS, creatorAttribution) ?? 0,
        additionalCostsDetail: creatorAttribution[DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION],

        ig_stories: getSplitMetric(DELIVERABLE_TYPE_INSTAGRAM_STORY, creatorAttribution),
        ig_stories_count: getDeliverableCount(DELIVERABLE_TYPE_INSTAGRAM_STORY, attribution),

        ig_posts: getSplitMetric(DELIVERABLE_TYPE_INSTAGRAM_POST, creatorAttribution),
        ig_posts_count: getDeliverableCount(DELIVERABLE_TYPE_INSTAGRAM_POST, attribution),

        ig_reels: getSplitMetric(DELIVERABLE_TYPE_INSTAGRAM_REEL, creatorAttribution),
        ig_reels_count: getDeliverableCount(DELIVERABLE_TYPE_INSTAGRAM_REEL, attribution),

        youtube_videos: getSplitMetric(DELIVERABLE_TYPE_YOUTUBE_VIDEO, creatorAttribution),
        youtube_videos_count: getDeliverableCount(DELIVERABLE_TYPE_YOUTUBE_VIDEO, attribution),

        tiktok_videos: getSplitMetric(DELIVERABLE_TYPE_TIKTOK_VIDEO, creatorAttribution),
        tiktok_videos_count: getDeliverableCount(DELIVERABLE_TYPE_TIKTOK_VIDEO, attribution),

        blog_posts: getSplitMetric(DELIVERABLE_TYPE_BLOG, creatorAttribution),
        blog_posts_count: getDeliverableCount(DELIVERABLE_TYPE_BLOG, attribution),

        other_tasks: getSplitMetric(DELIVERABLE_TYPE_OTHER, creatorAttribution),
        other_tasks_count: getDeliverableCount(DELIVERABLE_TYPE_OTHER, attribution)
      };
    });
    await put(url, { data: mappedData }, { suppressGlobalNotification: true });
    dispatch(success(__t('campaign_cost_attribution_reducer.saved_successfully')));
  } catch (e) {
    logError(e);
    dispatch(danger(__t('campaign_cost_attribution_reducer.save_error')));
  } finally {
    dispatch(hideLoading(url));
  }
};

export default createReducer<CampaignCostAttributionReducerState, any>('campaignCostAttribution', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [LOADING]: (state, { isLoading }) => ({
    ...state,
    isLoading
  }),
  [LOADED]: (state, { results }) => ({
    ...state,
    results,
    isLoading: false
  })
});
